import { useCallback, useEffect, useRef, useState } from 'react';

import type { TextRedactionResult } from '@dreryk/egabinet-ai-assistant-frontend-api';

import type { RedactionScope, RedactionState } from '../types';

import { useTranscriptionRedaction } from './useTranscriptionRedaction';

export type UseRedactionOptions = {
   field: () => string;
   /** Returns the full text content of the field. */
   getFieldText: () => string;
   /**
    * Returns the current selection within the textarea, or null if nothing
    * meaningful is selected (collapsed cursor or no selection).
    */
   getSelectionRange: () => { start: number; end: number; text: string } | null;
   /** Called when the user accepts the redaction proposal. */
   onAccept: (redactedText: string, scope: RedactionScope) => void;
   /**
    * Called when redact() is triggered but there is no text to redact
    * (empty field, empty selection). Use to show a warning to the user.
    */
   onEmpty?: VoidFunction;
};

export type UseRedactionResult = {
   redactionState: RedactionState;
   scope: RedactionScope | null;
   redactedText: string | null;
   originalText: string | null;
   isLocked: boolean;
   redact: VoidFunction;
   accept: VoidFunction;
   reject: VoidFunction;
   retry: VoidFunction;
};

/**
 * Resolves the text scope to redact — selection if one exists, full field otherwise.
 * Returns null if there is nothing to redact.
 */
function resolveRedactionScope(
   getFieldText: () => string,
   getSelectionRange: () => { start: number; end: number; text: string } | null
): RedactionScope | null {
   const selection = getSelectionRange();
   if (selection && selection.text.trim().length > 0) {
      return {
         kind: 'selection',
         text: selection.text,
         selectionStart: selection.start,
         selectionEnd: selection.end,
      };
   }
   const fullText = getFieldText();
   if (!fullText.trim()) {
      return null;
   }
   return { kind: 'full-field', text: fullText };
}

// eslint-disable-next-line max-lines-per-function,max-statements
export const useRedaction = (options: UseRedactionOptions): UseRedactionResult => {
   const [redactionState, setRedactionState] = useState<RedactionState>('idle');
   const [scope, setScope] = useState<RedactionScope | null>(null);
   const [redactedText, setRedactedText] = useState<string | null>(null);
   const [originalText, setOriginalText] = useState<string | null>(null);
   const [previousRejected, setPreviousRejected] = useState(false);

   // Stable refs — callbacks close over refs so useCallback deps stay minimal
   const getFieldTextRef = useRef(options.getFieldText);
   getFieldTextRef.current = options.getFieldText;
   const getSelectionRangeRef = useRef(options.getSelectionRange);
   getSelectionRangeRef.current = options.getSelectionRange;
   const onAcceptRef = useRef(options.onAccept);
   onAcceptRef.current = options.onAccept;
   const onEmptyRef = useRef(options.onEmpty);
   onEmptyRef.current = options.onEmpty;
   const fieldRef = useRef(options.field);
   fieldRef.current = options.field;
   const previousRejectedRef = useRef(previousRejected);
   previousRejectedRef.current = previousRejected;
   const redactedTextRef = useRef(redactedText);
   redactedTextRef.current = redactedText;
   const scopeRef = useRef(scope);
   scopeRef.current = scope;

   const redactionCommand = useTranscriptionRedaction({
      onSuccess: (result: TextRedactionResult) => {
         setRedactedText(result.redactedText);
         setRedactionState('proposal');
      },
   });
   const redactionCommandRef = useRef(redactionCommand);
   redactionCommandRef.current = redactionCommand;

   useEffect(() => {
      if (redactionCommand.error && redactionState === 'loading') {
         setRedactionState('error');
      }
   }, [redactionCommand.error, redactionState]);

   const redact = useCallback(() => {
      const currentScope = resolveRedactionScope(getFieldTextRef.current, getSelectionRangeRef.current);
      if (!currentScope) {
         onEmptyRef.current?.();
         return;
      }
      const context = previousRejectedRef.current ? JSON.stringify({ previousRejected: true }) : undefined;
      setScope(currentScope);
      setOriginalText(currentScope.text);
      setRedactedText(null);
      setRedactionState('loading');
      redactionCommandRef.current.execute({
         field: fieldRef.current(),
         text: currentScope.text,
         context: context,
      });
   }, []);

   const accept = useCallback(() => {
      // Read via ref — never inside an updater, so onAccept never fires during render.
      const currentScope = scopeRef.current;
      const accepted = redactedTextRef.current;
      if (currentScope && accepted) {
         onAcceptRef.current(accepted, currentScope);
      }
      setScope(null);
      setRedactionState('idle');
      setRedactedText(null);
      setOriginalText(null);
      setPreviousRejected(false);
   }, []);

   const reject = useCallback(() => {
      setRedactionState('idle');
      setScope(null);
      setRedactedText(null);
      setOriginalText(null);
      setPreviousRejected(true);
   }, []);

   const retry = useCallback(() => {
      setPreviousRejected(true);
      // Use existing scope text if available (don't re-read selection after reject)
      const text =
         scopeRef.current?.text ?? resolveRedactionScope(getFieldTextRef.current, getSelectionRangeRef.current)?.text;
      if (!text?.trim()) {
         return;
      }
      setOriginalText(text);
      setRedactedText(null);
      setRedactionState('loading');
      redactionCommandRef.current.execute({
         field: fieldRef.current(),
         text: text,
         context: JSON.stringify({ previousRejected: true }),
      });
   }, []);

   return {
      redactionState: redactionState,
      scope: scope,
      redactedText: redactedText,
      originalText: originalText,
      isLocked: redactionState === 'loading',
      redact: redact,
      accept: accept,
      reject: reject,
      retry: retry,
   };
};
